import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";

import {
  Form,
  Input,
  FormFeedback,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";

//redux
import { useSelector, useDispatch } from "react-redux";
import withRouter from "components/_Common/withRouter";
import toastr from "toastr";

// Formik validation
import * as Yup from "yup";
import { useFormik } from "formik";

// actions
import { addAspirationText, editAspirationText } from "store/actions";
import { fetchLanguageList } from "helpers/backend_api_helper";
import { useLocation } from "react-router-dom";
import { withTranslation } from "react-i18next";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

const TextAddEditModal = (props) => {
  const { modal, toggle, isEdit, itemData, fetchQuestionListData, tableData } =
    props;

  const [aspirationTextData, setAspirationTextData] = useState();
  const [languageList, setLanguageList] = useState();
  const { state } = useLocation();
  const [textTemplate, setTextTemplate] = useState(aspirationTextData?.text);

  const { error, success, loading, editLoading } = useSelector((state) => ({
    loading: state.AspirationQuestion.loading,
    error: state.AspirationQuestion.error,
    success: state.AspirationQuestion.success,
    editLoading: state.AspirationQuestion.editLoading,
  }));
  const dispatch = useDispatch();

  const cleanEditorText = (html) => {
  return html
    // remove ALL formatting tags
    .replace(/<\/?(p|br|strong|b|i|em|u|span|div|blockquote|h[1-6])[^>]*>/gi, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/(\r\n|\n|\r)+/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim();
};


  useEffect(() => {
    setAspirationTextData(itemData);
  }, [itemData]);

  // fetch language
  const fetchLanguageLists = async () => {
    const listLang = await fetchLanguageList();
    setLanguageList(listLang?.data);
  };

  useEffect(() => {
    fetchLanguageLists();
  }, []);

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      language:
        aspirationTextData?.languageId || (languageList && languageList[0]?.id),
      text: aspirationTextData?.text || "",
      is_active: aspirationTextData?.is_active || "Y",
    },
    validationSchema: Yup.object({
      language: Yup.string().required(props.t("Please Enter Language")),
      is_active: Yup.string().required(props.t("Please Enter Text Status")),
    }),
    onSubmit: (values, { resetForm }) => {
      if (!textTemplate) {
        toastr.error(props.t("Please Enter Text"));
        return;
      }

      values = {
        ...values,
        question_id: state?.questionData?.id,
        text: textTemplate,
      };
      if (isEdit) {
        const languageExists = tableData?.questions?.some(
          (item) =>
            item.languageId == Number(values.language) &&
            values.language != itemData.languageId
        );
        if (languageExists) {
          return toastr.error(
            "Question text already exists with the selected language."
          );
        }
        dispatch(
          editAspirationText(values, aspirationTextData?.id, (res) => {
            if (res.status === "success") {
              toastr.success(res.message);
              resetForm({
                language: "",
                text: "",
                is_active: "",
              });
              fetchQuestionListData();
              setTextTemplate();
              toggle();
            } else {
              toastr.error(res.message);
              setTextTemplate();
              toggle();
            }
          })
        );
      } else {
        const languageExists = tableData?.questions?.some(
          (item) => item.languageId == values.language
        );

        if (languageExists) {
          return toastr.error(
            "Question text already exists with the selected language."
          );
        }
        dispatch(
          addAspirationText(values, props?.router?.navigate, (res) => {
            if (res?.status === "success") {
              toastr.success(res.message);
              resetForm({
                language: "",
                text: "",
                is_active: "",
              });
              fetchQuestionListData();
              setTextTemplate();
              toggle();
            } else {
              toastr.error(res.message);
              setTextTemplate();
              toggle();
            }
          })
        );
      }
    },
  });

  const editorConfiguration = {
    placeholder: props.t("Enter Text"),
    toolbar: [
      "bold",
      "italic",
      "underline",
      "strikethrough",
      "subscript",
      "superscript",
    ],
  };

  return (
    <React.Fragment>
      <Modal
        isOpen={modal}
        toggle={() => {
          toggle();
          setTextTemplate();
        }}
      >
        <ModalHeader
          tag="h4"
          toggle={() => {
            toggle();
            setTextTemplate();
          }}
        >
          {!!isEdit
            ? props.t("Edit Aspiration Text")
            : props.t("Add Aspiration Text")}
        </ModalHeader>
        <ModalBody>
          <Form
            className="form-horizontal"
            onSubmit={(e) => {
              e.preventDefault();
              validation.handleSubmit();
              return false;
            }}
          >
            <div className="mb-3 d-block">
              <Label className="form-label m-0 pb-1">
                {props.t("Language")}:
              </Label>
              <Input
                name="language"
                className="form-control"
                type="select"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.language || ""}
                invalid={
                  validation.touched.language && validation.errors.language
                    ? true
                    : false
                }
              >
                <option value="" disabled>
                  {props.t("Select Language")}
                </option>
                {languageList?.map((data, key) => {
                  return (
                    <option key={key} value={data?.id}>
                      {data?.label}
                    </option>
                  );
                })}
              </Input>
              {validation.touched.language && validation.errors.language ? (
                <FormFeedback type="invalid">
                  {validation.errors.language}
                </FormFeedback>
              ) : null}
            </div>

            <div className="mb-3 d-block">
              <Label className="form-label m-0 pb-1">{props.t("Text")}:</Label>

              <CKEditor
                config={editorConfiguration}
                editor={ClassicEditor}
                data={aspirationTextData?.text ? aspirationTextData?.text : ""}
                // onChange={(event, editor) => {
                //   const data = editor.getData();
                //   setTextTemplate(data);
                // }}
                onChange={(event, editor) => {
                  const raw = editor.getData();
                  const cleaned = cleanEditorText(raw);
                  setTextTemplate(cleaned);
                }}
              />
            </div>
            <div className="mb-3 d-flex align-items-center gap-3">
              <Label className="form-label m-0">{props.t("Status")}:</Label>
              <div className="d-flex align-items-center gap-2">
                <Input
                  id="yes"
                  name="is_active"
                  type="radio"
                  checked={validation.values.is_active === "Y"}
                  value="Y"
                  onClick={validation.handleChange}
                  onBlur={validation.handleBlur}
                  invalid={
                    validation.touched.is_active && validation.errors.is_active
                      ? true
                      : false
                  }
                />
                <Label for="yes" className="form-label m-0">
                  {props.t("Active")}
                </Label>
                <Input
                  id="no"
                  name="is_active"
                  checked={validation.values.is_active === "N"}
                  value="N"
                  type="radio"
                  onClick={validation.handleChange}
                  onBlur={validation.handleBlur}
                  invalid={
                    validation.touched.is_active && validation.errors.is_active
                      ? true
                      : false
                  }
                />
                <Label for="no" className="form-label m-0">
                  {props.t("In-Active")}
                </Label>
              </div>
              {validation.touched.is_active && validation.errors.is_active ? (
                <FormFeedback type="invalid">
                  {validation.errors.is_active}
                </FormFeedback>
              ) : null}
            </div>

            {editLoading ? (
              <div className="btn-login-admin mt-3 batches-add-modal-btn">
                <button
                  type="button"
                  className="btn btn-primary btn-block"
                  disabled
                >
                  <i className="bx bx-loader bx-spin font-size-16 align-middle me-2"></i>{" "}
                  {props.t("Submit")}
                </button>
              </div>
            ) : (
              <div className="btn-login-admin mt-3 batches-add-modal-btn">
                <button
                  className="btn btn-primary btn-block"
                  type="submit"
                  // disabled
                >
                  {props.t("Submit")}
                </button>
              </div>
            )}
          </Form>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

TextAddEditModal.propTypes = {
  history: PropTypes.object,
  t: PropTypes.any,
};
export default withTranslation()(TextAddEditModal);
